import React, { ComponentType, CSSProperties } from 'react';
import clsx from 'clsx';
import { DropdownImage, DropdownItem, LinkProps } from './MegaNav.types';
import { themeTokens } from './MegaNav.tokens';

const getDropdownImages = (item: DropdownItem): DropdownImage[] => {
    if (item.images?.length) return item.images;
    if (item.image) return [item.image];

    return [];
};

export const DesktopDropDownMenu = ({
    submenuscontent,
    setIsDrawerOpen,
    handleClick,
    Link,
    tokens,
}: {
    submenuscontent: DropdownItem[];
    setIsDrawerOpen?: (v: boolean) => void;
    handleClick?: () => void;
    Link: ComponentType<LinkProps>;
    tokens: typeof themeTokens.light;
}) => {
    const validItems = submenuscontent?.filter(
        (item) =>
            (item.submenu && item.submenu.length > 0) ||
            getDropdownImages(item).length > 0
    );

    if (!validItems || validItems.length === 0) return null;

    const submenuCount = validItems.reduce(
        (count, item) => count + (item.submenu?.length ?? 0),
        0
    );
    const isDense = validItems.length > 4 || submenuCount > 24;
    const hasImages = validItems.some((item) => getDropdownImages(item).length > 0);
    const menuStyle: CSSProperties = {
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${hasImages ? '13.5rem' : isDense ? '10.5rem' : '12.5rem'}, 1fr))`,
        gap: isDense ? '20px 28px' : '28px 40px',
        alignItems: 'start',
        maxHeight: 'min(68vh, 520px)',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        paddingRight: 8,
    };
    const imageGridStyle: CSSProperties = {
        maxWidth: isDense ? '14rem' : '18rem',
        width: '100%',
    };
    const headingStyle: CSSProperties = {
        color: 'inherit',
        display: 'block',
        fontSize: isDense ? 13 : 14,
        fontWeight: 700,
        lineHeight: isDense ? '17px' : '20px',
        overflowWrap: 'anywhere',
        textDecoration: 'none',
    };
    const linkStyle: CSSProperties = {
        color: 'inherit',
        display: 'block',
        fontSize: isDense ? 13 : 14,
        lineHeight: isDense ? '17px' : '20px',
        overflowWrap: 'anywhere',
        textDecoration: 'none',
    };

    return (
        <div style={menuStyle}>
            {validItems.map((item, index) => {
                const images = getDropdownImages(item);

                return (
                    <section key={index} className="min-w-0">
                        <div
                            className={clsx(
                                'mb-2.5 min-w-0',
                                tokens.textPrimary
                            )}
                        >
                            <Link
                                href={item.headingUrl || item.href || '#'}
                                onClick={() => setIsDrawerOpen?.(false)}
                                style={headingStyle}
                            >
                                {item.heading}
                            </Link>
                        </div>

                        {images.length > 0 ? (
                            <div className="mb-3 hidden gap-2 md:grid" style={imageGridStyle}>
                                {images.map((image, imageIndex) => {
                                    const imageNode = (
                                        <img
                                            src={image.src}
                                            alt={image.alt ?? image.label ?? ''}
                                            loading="lazy"
                                            className="aspect-[4/3] w-full rounded-md object-cover"
                                        />
                                    );

                                    return image.href ? (
                                        <Link
                                            key={`${image.src}-${imageIndex}`}
                                            href={image.href}
                                            className="group block overflow-hidden rounded-md"
                                            aria-label={image.label ?? image.alt}
                                            onClick={() => setIsDrawerOpen?.(false)}
                                        >
                                            {imageNode}
                                        </Link>
                                    ) : (
                                        <div
                                            key={`${image.src}-${imageIndex}`}
                                            className="overflow-hidden rounded-md"
                                        >
                                            {imageNode}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}

                        {item.submenu?.length ? (
                            <ul className={clsx(isDense ? 'space-y-1' : 'space-y-1.5')}>
                                {item.submenu.map(({ label, href }, i) => (
                                    <li
                                        key={i}
                                        className={tokens.textSecondary}
                                        onClick={() => {
                                            setIsDrawerOpen?.(false);
                                            handleClick?.();
                                        }}
                                    >
                                        <Link
                                            href={href}
                                            className={clsx(
                                                'rounded-sm px-1 py-0.5 transition-colors',
                                                tokens.textSecondary,
                                                tokens.hoverBg
                                            )}
                                            style={linkStyle}
                                        >
                                            {label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                    </section>
                );
            })}
        </div>
    );
};
